import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MemberMissionView from '../../components/mission-control/MemberMissionView';
import LeaderMissionView from '../../components/mission-control/LeaderMissionView';
import type {
  MissionControlConfig,
  MissionControlCycle,
  MissionControlObjective,
  MissionControlObjectiveContact,
  Account,
  Contact,
} from '../../types';

export interface EnrichedObjective extends MissionControlObjective {
  account: Account;
  contacts: EnrichedObjectiveContact[];
  use_cases_processed: boolean;
}

export interface EnrichedObjectiveContact extends MissionControlObjectiveContact {
  contact: Contact;
}

export interface EnrichedCycle extends MissionControlCycle {
  objectives: EnrichedObjective[];
}

export interface TeamMember {
  user_id: string;
  name: string;
  email: string;
  cycles: MissionControlCycle[];
  rollingScore: number;
  currentCycle: EnrichedCycle | null;
}

interface MissionControlOverview {
  config: MissionControlConfig | null;
  cycles: MissionControlCycle[];
  accounts: Account[];
  contacts: Contact[];
  contact_priorities: Record<string, string>;
  current_cycle: EnrichedCycle | null;
}

export default function MissionControl() {
  const { user, organisation, membership } = useAuth();
  const { apiGet } = useApi();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<MissionControlConfig | null>(null);
  const [myCycles, setMyCycles] = useState<MissionControlCycle[]>([]);
  const [currentCycle, setCurrentCycle] = useState<EnrichedCycle | null>(null);
  const [myAccounts, setMyAccounts] = useState<Account[]>([]);
  const [myContacts, setMyContacts] = useState<Contact[]>([]);
  const [contactPriorities, setContactPriorities] = useState<Record<string, string>>({});

  const role = membership?.role;
  const isLeaderOrAbove = role === 'leader' || role === 'admin' || role === 'executive';

  const loadMemberData = useCallback(async () => {
    if (!user?.id || !organisation?.id) return;
    setLoading(true);

    try {
      const data = await apiGet<MissionControlOverview>(
        `/v1/mission-control/overview?org_id=${organisation.id}`,
      );
      setConfig(data.config);
      setMyCycles(data.cycles);
      setMyAccounts(data.accounts);
      setMyContacts(data.contacts);
      setContactPriorities(data.contact_priorities || {});
      setCurrentCycle(data.current_cycle);
    } finally {
      setLoading(false);
    }
  }, [user?.id, organisation?.id, apiGet]);

  useEffect(() => {
    if (!isLeaderOrAbove) {
      loadMemberData();
    } else {
      setLoading(false);
    }
  }, [isLeaderOrAbove, loadMemberData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isLeaderOrAbove) {
    return (
      <LeaderMissionView
        orgId={organisation!.id}
        userId={user!.id}
        role={role!}
      />
    );
  }

  return (
    <MemberMissionView
      config={config}
      myCycles={myCycles}
      currentCycle={currentCycle}
      myAccounts={myAccounts}
      myContacts={myContacts}
      contactPriorities={contactPriorities}
      orgId={organisation!.id}
      userId={user!.id}
      onRefresh={loadMemberData}
    />
  );
}
